
/*
dialog.js
Augments the <dialog> element.
*/

import clamp           from 'fn/clamp.js';
import create          from 'dom/create.js';
import events          from 'dom/events.js';
import delegate        from 'dom/delegate.js';
import focus           from 'dom/focus.js';
import isTargetEvent   from 'dom/is-target-event.js';
import style           from 'dom/style.js';
import rect            from 'dom/rect.js';
import trigger         from 'dom/trigger.js';
import { disableScroll, enableScroll } from 'dom/scroll.js';
import { trapFocus, untrapFocus }      from 'dom/focus.js';
import { setToggleSource } from '../events/toggle.js';
import { actions }     from '../attributes/name=toggle.js';


const onceOptions = { once: true };
const isSafari    = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
let requiresDialogToggleEvent = isSafari;

document.addEventListener('toggle', (e) => {
    if (ToggleEvent.isPrototypeOf(e) && e.target.matches('dialog')) {
        console.log('Disabling custom toggle events for dialogs');
        requiresDialogToggleEvent = false;
    }
});


// As it is possible to have multiple dialogs open, we must enumerate them
let n = 0;
let timer;

// Track last hashchange time
let hashtime = -1000;
events('hashchange', (e) => hashtime = e.timeStamp);

function disableDocumentScroll() {
    if (n === 0) {
        // In Linux Chrome (at least), disabling scroll interrupts scrolling
        // that is launched on any internal elements responding to hash change,
        // so we must delay it. I wish we could ask for scroll events and wait
        // until they are finished, but we cannot listen to scroll events inside
        // of a shadow DOM, so we are forced to use setTimeout.
        const delay = clamp(0, 1000, window.performance.now() - hashtime);
        timer = setTimeout(disableScroll, delay);
    }

    // As it is possible to have multiple dialogs open, we must enumerate them
    ++n;
}

function enableDocumentScroll() {
    // n shouldn't drop below 0, but if there's an error somewhere it's possible
    n = clamp(0, Infinity, n - 1);

    // Enable scrolling on document element
    if (n === 0) {
        clearTimeout(timer);
        enableScroll();
    }
}

function focusElement(element) {
    // Focus the first focusable element, if element does not already contain focus
    if (element !== document.activeElement && !element.contains(document.activeElement)) {
        // The click that activated this target is not over yet, wait two frames
        // to focus the element. I don't know why we need three. Two is enough
        // in Safari, Chrome seems to like three, to be reliable. Not sure what
        // we are waiting for here. No sir, I don't like it.
        requestAnimationFrame(() => requestAnimationFrame(() => requestAnimationFrame(() =>
            focus(element)
        )));
    }
}

export function open(element, target) {
    // Is the element closed?
    if (element.open) { return; }

    // Set related target of open event. This should not be required once toggle
    // event has a reliable source property.
    setToggleSource(target);

    // Implement our own modal attribute. We do this rather than use .showModal()
    // because it is impossible to show, for example, non-modal message dialogs
    // on top of .showModal() dialogs
    const mode = element.getAttribute('mode');

    if (mode === 'modal') {
        const focused = document.activeElement;

        // A modal dialog automatically has focus trapped in the top layer
        element.showModal();
        disableDocumentScroll();

        // Dialogs have a 'close' event and a 'toggle' event, but the powers
        // that be have decided they should not bubble. This is nonsense, it's
        // not like they're high frequency.
        element.addEventListener('close', (e) => {
            enableDocumentScroll();
            focused.focus();
        }, onceOptions);
    }
    else if (mode === 'overlay') {
        element.show();
        disableDocumentScroll();
        trapFocus(element);

        // Add backdrop to the body
        const backdrop = document.body.querySelector('.dialog-backdrop') || create('div', {
            ariaHidden: 'true',
            class: 'dialog-backdrop'
        });

        // Make sure the backdrop stays at the end of the document
        document.body.append(backdrop);

        // Dialogs do have a 'close' event, it does not bubble.
        element.addEventListener('close', (e) => {
            // Reenable document scroll
            enableDocumentScroll();
            // Return focus to wherever it was before
            untrapFocus();
        }, onceOptions);
    }
    else {
        // Default unaugmented behaviour
        element.show();
    }

    if (requiresDialogToggleEvent) {
        element.addEventListener('close', (e) => {
            if (!requiresDialogToggleEvent) return;
            trigger({ type: 'toggle', newState: 'closed', oldState: 'open', source: target }, element);
        }, onceOptions);

        trigger({ type: 'toggle', newState: 'open', oldState: 'closed', source: target }, element);
    }

    // Return state of dialog
    return true;
}

export function close(element, target) {
    // Is the element open?
    if ('open' in element && !element.open) { return; }

    // Immediately (optimistically?) reenable document scroll and reset focus
    const mode = element.getAttribute('mode');
    if (mode === 'modal') {
        enableDocumentScroll();
    }
    else if (mode === 'overlay') {
        enableDocumentScroll();
        untrapFocus();
    }

    // And take it off when the closing transition is over
    const ends = events('transitionend', element)
    .filter(isTargetEvent)
    .slice(0, 1)
    .each((e) => {
        // Set related target of close event.
        setToggleSource(target);
        element.close();
        element.classList.remove('closing');
    });

    element.classList.add('closing');
    const duration = parseFloat(style('transition-duration', element));
    if (duration) return;

    // If there is no transition applied close immediately
    ends.stop();

    // Set related target of close event.
    setToggleSource(target);
    element.close();
    element.classList.remove('closing');

    // Return state of dialog
    return false;
}

actions('dialog', {
    locate: open,
    open:   open,
    close:  close,
    toggle: (element, target) => element.open ?
        close(element, target) :
        open(element, target)
});
